import { Dispatch, SetStateAction, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { FormAutocompleteController, FormInputNumberController } from '@/components';
import { Ingredient, IngredientWithAmount, useIngredients, useProduct } from '@/core';

import { styles } from './styles';

type FormIngredient = {
  id: number;
  name: string;
  unit: string;
};

type IngredientFormData = {
  ingredient: FormIngredient;
  amount: number;
};

const schema = z.object({
  ingredient: z.object({
    id: z.number(),
    name: z.string(),
    unit: z.string(),
  }),
  amount: z.number(),
});

export const EditableIngredient = ({
  ingredientWithAmount,
  ingredientsWithAmount,
  setIngredientsWithAmount,
}: {
  ingredientWithAmount: IngredientWithAmount;
  ingredientsWithAmount: IngredientWithAmount[];
  setIngredientsWithAmount: Dispatch<SetStateAction<IngredientWithAmount[]>>;
}) => {
  const { t } = useTranslation();
  const { useIngredientsSearch } = useIngredients();
  const [options, setOptions] = useState<Ingredient[]>([]);

  const {
    queries: { useProductIngredients },
  } = useProduct();
  const { id } = useParams<{ id: string }>();
  const { data: ingredientsResponse } = useProductIngredients(Number(id), true);

  const methods = useForm<IngredientFormData>({
    defaultValues: {
      ingredient: ingredientWithAmount.ingredient,
      amount: ingredientWithAmount.amount,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const handleRemoveIngredientClick = () => {
    setIngredientsWithAmount((prev: IngredientWithAmount[]) =>
      prev.filter((el) => el.ingredient.id !== ingredientWithAmount.ingredient.id),
    );
  };

  const handleSaveNewIngredient = () => {
    const { amount, ingredient } = methods.getValues();
    if (!ingredient) return;
    setIngredientsWithAmount((prev: IngredientWithAmount[]) => {
      const updatedIngredients = prev.map((item) => {
        if (item.ingredient.id === ingredientWithAmount.ingredient.id) {
          return {
            ingredient: {
              ...ingredient,
              description: '',
              price: '0',
              is_active: true,
            },
            amount,
          };
        }
        return item;
      });
      return updatedIngredients;
    });
  };

  return (
    <FormProvider {...methods}>
      <TableRow>
        <TableCell align="left">
          <FormAutocompleteController<Ingredient>
            label={t('INGREDIENT.CHOOSE')}
            controllerName={'ingredient'}
            options={options}
            fetchOptions={useIngredientsSearch}
            setOptions={setOptions}
            getOptionLabel={(option) => option?.name ?? ''}
            getOptionKey={(option) => option.id.toString()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fieldOnBlur={handleSaveNewIngredient}
            renderInput={() => <div></div>}
            optionsToFilterOut={ingredientsWithAmount
              .concat(ingredientsResponse?.data ?? [])
              .map((el) => el.ingredient)}
          />
        </TableCell>
        <TableCell align="left">
          <Stack direction="row" gap={1} alignItems="center">
            <FormInputNumberController
              label={''}
              controllerName={'amount'}
              fieldOnBlur={handleSaveNewIngredient}
              decimalPlaces={2}
              sx={{ width: '150px' }}
            />
            <Typography variant="body2" color="text.secondary">
              {methods.getValues()?.ingredient.unit}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="right" sx={styles.shrinkCell}>
          <IconButton size="medium" onClick={handleRemoveIngredientClick}>
            <span className="material-symbols-rounded">close</span>
          </IconButton>
        </TableCell>
      </TableRow>
    </FormProvider>
  );
};
